import React, { useEffect, useState } from 'react';
import { Box, FormControl, InputLabel } from '@mui/material';

import { apiRequest } from '@/src/services';
import { getValidSelectValue, getZeroOption, type Option, Constants } from '@/src/utils';
import {
    type StatusReportInitialOptions,
    type StatusReportSchoolClassOptions,
    type ClassOption,
    type StatusReportFilterState,
    type ExamOption,
} from '../models/statusReportFilterDTO';
import {
    SimpleButton,
    SimpleSingleSelect,
    SimpleRadio,
    ErrorMessage,
    FilterIcon,
    ICON_COLORS,
    FilterBarContainer,
} from '@/src/ui';

type GroupBy = 'examClass' | 'exam';

interface StatusReportFiltersProps {
    refreshTableData: (filters: StatusReportFilterState) => void | Promise<void>;
    filterState: StatusReportFilterState;
    setFilterState: React.Dispatch<React.SetStateAction<StatusReportFilterState>>;
}

const AllOption = getZeroOption('All');
const LoadingOption = getZeroOption('Loading...');
const DefaultSchoolOption = getZeroOption('Select School');
const DefaultClassOption = getZeroOption('Select Class');
const DefaultGradeOption = getZeroOption('Select Grade');
const NoClassOption = getZeroOption('No Classes Available');
const NoExamOption = getZeroOption('No Exams Available');
const SelectDistrictOption = getZeroOption('Select District');

/** Map GroupBy <-> numeric ids because SimpleSingleSelect.value is number-typed */
const GROUP_BY_TO_ID: Record<GroupBy, number> = { examClass: 0, exam: 1 };
const ID_TO_GROUP_BY: Record<number, GroupBy> = { 0: 'examClass', 1: 'exam' };

const groupByOptions = [
    { id: GROUP_BY_TO_ID.examClass, name: 'By Class' },
    { id: GROUP_BY_TO_ID.exam, name: 'By Exam' },
];

const StatusReportFilters: React.FC<StatusReportFiltersProps> = ({
    refreshTableData,
    filterState,
    setFilterState,
}) => {
    const [errorMsg, setErrorMsg] = useState<string | null>(null);

    const [gradeLevelMasterOptions, setGradeLevelMasterOptions] = useState<Option[]>([
        LoadingOption,
    ]);
    const [districtOptions, setDistrictOptions] = useState<Option[]>([LoadingOption]);
    const [examTypeMasterOptions, setExamTypeMasterOptions] = useState<Option[]>([AllOption]);
    const [examOptions, setExamOptions] = useState<Option[]>([AllOption]);
    const [schoolOptions, setSchoolOptions] = useState<Option[]>([DefaultSchoolOption]);
    const [classOptions, setClassOptions] = useState<Option[]>([AllOption]);
    const [classData, setClassData] = useState<ClassOption[]>([]);
    const [examDataAll, setExamDataAll] = useState<ExamOption[]>([]);
    const [filtersLoading, setFiltersLoading] = useState(true);

    const [fieldsChanged, setFieldsChanged] = useState(false);

    const requiredFields = ['districtId', 'schoolId', 'gradeLevelMasterId'] as const;
    function getMissingFields(fs: any): string[] {
        return requiredFields
            .filter((key) => !fs[key] || Number(fs[key]) === 0)
            .map((key) => Constants.fieldLabels[key]);
    }

    const updateClassOptions = (
        data: Array<ClassOption>,
        gradeLevelMasterId: number | string,
        schoolId: number | string
    ) => {
        // Only calculate classes if grade + school are selected (not default/All)
        if (
            !gradeLevelMasterId ||
            gradeLevelMasterId === AllOption.id ||
            !schoolId ||
            schoolId === DefaultSchoolOption.id
        ) {
            setClassOptions([DefaultClassOption]);
            setFilterState((prev) => ({ ...prev, classId: Number(DefaultClassOption.id) }));
            return;
        }

        const newClassOptions = data
            .filter((option) => {
                if (schoolId && option.schoolId !== Number(schoolId)) return false;
                const glids = option.gradeLevelMasterIds;
                const doGradeOptionsExist = glids && glids.length > 0;
                if (
                    gradeLevelMasterId &&
                    doGradeOptionsExist &&
                    !glids.includes(Number(gradeLevelMasterId))
                )
                    return false;
                return true;
            })
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((option) => ({ id: option.id, name: option.name }));

        if (!newClassOptions.length) {
            setClassOptions([NoClassOption]);
            setFilterState((prev) => ({ ...prev, classId: Number(NoClassOption.id) }));
            return;
        }

        setClassOptions([AllOption, ...newClassOptions]);
        setFilterState((prev) => ({ ...prev, classId: Number(AllOption.id) }));
    };

    const fetchSchoolClassOptions = async (
        districtId: number | string,
        savedSchoolId?: number,
        savedClassId?: number
    ) => {
        if (!districtId) {
            setSchoolOptions([DefaultSchoolOption]);
            setClassOptions([DefaultClassOption]);
            setFilterState((prev) => ({
                ...prev,
                schoolId: Number(DefaultSchoolOption.id),
                classId: Number(DefaultClassOption.id),
            }));
            return;
        }

        try {
            const res = await apiRequest<StatusReportSchoolClassOptions>(
                'GET',
                `utility/filterData?getSchool=true&getClass=true&districtId=${districtId}`
            );
            const data = res.data;

            const sortedSchools = data.schoolOptions.sort((a, b) => a.name.localeCompare(b.name));
            setSchoolOptions([DefaultSchoolOption, ...sortedSchools]);
            const validSchoolId =
                savedSchoolId && sortedSchools.some((opt) => opt.id === savedSchoolId)
                    ? savedSchoolId
                    : Number(DefaultSchoolOption.id);
            setFilterState((prev) => ({ ...prev, schoolId: Number(validSchoolId) }));

            const sortedClassData = data.classOptions.sort((a, b) => a.name.localeCompare(b.name));
            setClassData(sortedClassData);
            let filteredClassOptions = sortedClassData;
            if (validSchoolId && validSchoolId !== Number(DefaultSchoolOption.id)) {
                filteredClassOptions = sortedClassData.filter(
                    (opt) => opt.schoolId === Number(validSchoolId)
                );
            }
            // Further filter by grade if set in filterState
            const gradeId = filterState.gradeLevelMasterId;
            if (gradeId && gradeId !== 0) {
                filteredClassOptions = filteredClassOptions.filter(
                    (opt) =>
                        Array.isArray(opt.gradeLevelMasterIds) &&
                        opt.gradeLevelMasterIds.includes(gradeId)
                );
            }
            filteredClassOptions = filteredClassOptions.sort((a, b) =>
                a.name.localeCompare(b.name)
            );
            const classOptionsArr = filteredClassOptions.length
                ? [
                      DefaultClassOption,
                      ...filteredClassOptions.map((opt) => ({ id: opt.id, name: opt.name })),
                  ]
                : [NoClassOption];
            setClassOptions(classOptionsArr);

            const validClassId =
                savedClassId && filteredClassOptions.some((opt) => opt.id === savedClassId)
                    ? savedClassId
                    : Number(DefaultClassOption.id);
            setFilterState((prev) => ({ ...prev, classId: Number(validClassId) }));
            setErrorMsg(null);
        } catch {
            setErrorMsg('Failed to fetch school and class options');
            setSchoolOptions([AllOption]);
            setClassOptions([AllOption]);
        }
    };

    useEffect(() => {
        (async function init() {
            try {
                const res = await apiRequest<StatusReportInitialOptions>(
                    'GET',
                    'utility/filterData?getGradeLevelMaster=true&getDistrict=true&getExamTypeMaster=true'
                );
                const data = res.data;
                setGradeLevelMasterOptions([DefaultGradeOption, ...data.gradeLevelMasterOptions]);
                const sortedDistricts = data.districtOptions.sort((a, b) =>
                    a.name.localeCompare(b.name)
                );
                setDistrictOptions([SelectDistrictOption, ...sortedDistricts]);
                setExamTypeMasterOptions([
                    AllOption,
                    ...data.examTypeMasterOptions.sort((a, b) => a.name.localeCompare(b.name)),
                ]);
                setFilterState((prev) => {
                    const next = {
                        ...prev,
                        districtId: prev.districtId || 0,
                        schoolId: prev.schoolId || Number(DefaultSchoolOption.id),
                        gradeLevelMasterId:
                            prev.gradeLevelMasterId || Number(DefaultGradeOption.id),
                        classId: prev.classId || Number(DefaultClassOption.id),
                        examTypeMasterId: prev.examTypeMasterId || Number(AllOption.id),
                        examId: prev.examId || Number(AllOption.id),
                    };
                    if (next.districtId && next.districtId !== 0) {
                        setTimeout(async () => {
                            await fetchSchoolClassOptions(
                                next.districtId,
                                next.schoolId,
                                next.classId
                            );
                            setFiltersLoading(false);
                        }, 0);
                    } else {
                        setFiltersLoading(false);
                    }
                    return next;
                });
            } catch {
                setDistrictOptions([AllOption]);
                setGradeLevelMasterOptions([DefaultGradeOption]);
                setExamTypeMasterOptions([AllOption]);
                setFiltersLoading(false);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [setFilterState]);

    const filterExamsForSelection = (
        exams: ExamOption[],
        schoolId: number | string,
        classId: number | string,
        examTypeMasterId: number | string
    ) => {
        if (
            !exams?.length ||
            !schoolId ||
            Number(schoolId) === Number(AllOption.id) ||
            !filterState.gradeLevelMasterId ||
            Number(filterState.gradeLevelMasterId) === Number(AllOption.id)
        ) {
            setExamOptions([AllOption]);
            setFilterState((prev) => ({ ...prev, examId: Number(AllOption.id) }));
            return;
        }

        const filtered = exams.filter((exam) => {
            if (!exam.schoolIds?.length) return false;

            if (
                schoolId &&
                Number(schoolId) !== Number(AllOption.id) &&
                !exam.schoolIds.includes(Number(schoolId))
            )
                return false;

            if (
                classId &&
                Number(classId) !== Number(AllOption.id) &&
                exam.classIds?.length &&
                !exam.classIds.includes(Number(classId))
            )
                return false;

            if (
                examTypeMasterId &&
                Number(examTypeMasterId) !== Number(AllOption.id) &&
                exam.examTypeMasterId !== Number(examTypeMasterId)
            )
                return false;

            return true;
        });

        const newExamOptions = filtered.length
            ? [AllOption, ...filtered.map((e) => ({ id: e.id, name: e.name }))]
            : [NoExamOption];

        setExamOptions(newExamOptions);

        // Keep selected examId if it still exists; otherwise reset
        setFilterState((prev) => ({
            ...prev,
            examId:
                filtered.length && prev.examId && filtered.some((e) => e.id === prev.examId)
                    ? prev.examId
                    : filtered.length
                      ? Number(AllOption.id)
                      : Number(NoExamOption.id),
        }));
    };

    const fetchExamOptions = async (
        examTypeMasterId: number | string,
        gradeLevelMasterId: number | string,
        districtId: number | string,
        schoolId: number | string
    ) => {
        if (
            !districtId ||
            Number(districtId) === Number(AllOption.id) ||
            !schoolId ||
            Number(schoolId) === Number(AllOption.id) ||
            !gradeLevelMasterId ||
            Number(gradeLevelMasterId) === Number(AllOption.id)
        ) {
            // Clear cached exams if prerequisites not met
            setExamDataAll([]);
            setExamOptions([AllOption]);
            setFilterState((prev) => ({ ...prev, examId: Number(AllOption.id) }));
            return;
        }

        try {
            setExamOptions([LoadingOption]);
            const res = await apiRequest<{ examOptions: ExamOption[] }>(
                'GET',
                `utility/filterData?getExam=true&gradeLevelMasterIds=${gradeLevelMasterId}&showPublishedExamsOnly=true`
            );
            const data = (res.data.examOptions ?? [])
                .filter((exam) => exam.schoolIds?.length)
                .sort((a, b) => a.name.localeCompare(b.name));

            setExamDataAll(data);
            // initial filtering with current school/class/examType
            filterExamsForSelection(
                data,
                schoolId,
                classData ? filterState.classId : filterState.classId,
                examTypeMasterId
            );
            setErrorMsg(null);
        } catch {
            setExamDataAll([]);
            setExamOptions([NoExamOption]);
            setFilterState((prev) => ({ ...prev, examId: Number(AllOption.id) }));
            setErrorMsg('Failed to load exam options');
        }
    };

    useEffect(() => {
        if (filterState.districtId && filterState.districtId !== Number(SelectDistrictOption.id)) {
            fetchSchoolClassOptions(
                filterState.districtId,
                filterState.schoolId,
                filterState.classId
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterState.districtId]);

    // district + school + grade -> fetch the EXAMS once (server call)
    useEffect(() => {
        const { examTypeMasterId, gradeLevelMasterId, districtId, schoolId } = filterState;
        void fetchExamOptions(examTypeMasterId, gradeLevelMasterId, districtId, schoolId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterState.districtId, filterState.schoolId, filterState.gradeLevelMasterId]);

    // When school/class/examType change, locally filter using cached examDataAll
    useEffect(() => {
        if (!examDataAll.length) return;
        filterExamsForSelection(
            examDataAll,
            filterState.schoolId,
            filterState.classId,
            filterState.examTypeMasterId
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterState.schoolId, filterState.classId, filterState.examTypeMasterId]);

    // Helper to show loading state for dropdowns
    const getLoadingProps = (opts: Option[], val: number, loading: boolean) =>
        loading
            ? { options: [LoadingOption], value: 0, disabled: true }
            : { options: opts, value: val };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
            <FilterBarContainer>
                <SimpleSingleSelect
                    name="district-select"
                    label="District *"
                    {...getLoadingProps(
                        districtOptions,
                        Number(
                            getValidSelectValue(
                                filterState.districtId,
                                districtOptions,
                                districtOptions[0]?.id ?? 0
                            )
                        ),
                        filtersLoading
                    )}
                    onChange={(e) => {
                        setFieldsChanged(true);
                        const newDistrictId = Number(e.target.value);
                        setFilterState({
                            ...filterState,
                            districtId: newDistrictId,
                            schoolId: Number(DefaultSchoolOption.id),
                            gradeLevelMasterId: Number(DefaultGradeOption.id),
                            classId: Number(DefaultClassOption.id),
                            examTypeMasterId: Number(AllOption.id),
                            examId: Number(AllOption.id),
                        });
                        fetchSchoolClassOptions(newDistrictId);
                        setExamOptions([AllOption]);
                    }}
                    sx={{ minWidth: 140, maxWidth: 280 }}
                />
                <SimpleSingleSelect
                    name="school-select"
                    label="School *"
                    {...getLoadingProps(
                        schoolOptions,
                        Number(
                            getValidSelectValue(
                                filterState.schoolId,
                                schoolOptions,
                                schoolOptions[0]?.id ?? 0
                            )
                        ),
                        filtersLoading
                    )}
                    onChange={(e) => {
                        setFieldsChanged(true);
                        const newSchoolId = Number(e.target.value);
                        setFilterState((prev) => ({
                            ...prev,
                            schoolId: newSchoolId,
                            gradeLevelMasterId: Number(DefaultGradeOption.id),
                            classId: Number(DefaultClassOption.id),
                        }));
                        updateClassOptions(classData, Number(DefaultGradeOption.id), newSchoolId);
                        if (examDataAll.length) {
                            filterExamsForSelection(
                                examDataAll,
                                newSchoolId,
                                Number(DefaultClassOption.id),
                                filterState.examTypeMasterId
                            );
                        }
                    }}
                    sx={{ minWidth: 140, maxWidth: 280 }}
                    disabled={
                        filtersLoading ||
                        !filterState.districtId ||
                        filterState.districtId === Number(AllOption.id)
                    }
                />
                <SimpleSingleSelect
                    name="grade-level-select"
                    label="Grade Level *"
                    {...getLoadingProps(
                        gradeLevelMasterOptions,
                        Number(
                            getValidSelectValue(
                                filterState.gradeLevelMasterId,
                                gradeLevelMasterOptions,
                                gradeLevelMasterOptions[0]?.id ?? 0
                            )
                        ),
                        filtersLoading
                    )}
                    onChange={(e) => {
                        setFieldsChanged(true);
                        const newGradeLevelMasterId = Number(e.target.value);
                        setFilterState((prev) => ({
                            ...prev,
                            gradeLevelMasterId: newGradeLevelMasterId,
                        }));
                        updateClassOptions(classData, newGradeLevelMasterId, filterState.schoolId);
                    }}
                    sx={{ minWidth: 140, maxWidth: 280 }}
                    disabled={
                        filtersLoading ||
                        !filterState.schoolId ||
                        filterState.schoolId === Number(DefaultSchoolOption.id)
                    }
                />
                <SimpleSingleSelect
                    name="class-select"
                    label="Class"
                    {...getLoadingProps(
                        classOptions,
                        Number(
                            getValidSelectValue(
                                filterState.classId,
                                classOptions,
                                classOptions[0]?.id ?? 0
                            )
                        ),
                        filtersLoading
                    )}
                    onChange={(e) => {
                        setFieldsChanged(true);
                        const newClassId = Number(e.target.value);
                        setFilterState((prev) => ({ ...prev, classId: newClassId }));
                        if (examDataAll.length) {
                            filterExamsForSelection(
                                examDataAll,
                                filterState.schoolId,
                                newClassId,
                                filterState.examTypeMasterId
                            );
                        }
                    }}
                    sx={{ minWidth: 140, maxWidth: 280 }}
                    disabled={
                        filtersLoading ||
                        !filterState.districtId ||
                        filterState.districtId === Number(AllOption.id) ||
                        !filterState.schoolId ||
                        filterState.schoolId === DefaultSchoolOption.id ||
                        !filterState.gradeLevelMasterId ||
                        filterState.gradeLevelMasterId === AllOption.id
                    }
                />
            </FilterBarContainer>

            <FilterBarContainer>
                <FormControl size="small" sx={{ minWidth: 140, maxWidth: 280, mb: 0.5, mt: '4px' }}>
                    <InputLabel
                        shrink
                        htmlFor="group-by-radio"
                        sx={{ background: 'white', px: 0.5 }}
                    >
                        View Mode
                    </InputLabel>
                    <Box
                        id="group-by-radio"
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            gap: 1.5,
                            border: (theme) => `1px solid ${theme.palette.grey[400]}`,
                            borderRadius: 1,
                            backgroundColor: (theme) => theme.palette.background.paper,
                            px: 2,
                            py: 1.5,
                            maxHeight: 40,
                            alignSelf: 'center',
                        }}
                    >
                        {groupByOptions.map((option) => (
                            <SimpleRadio
                                key={option.id}
                                name="group-by"
                                label={option.name}
                                value={String(option.id)}
                                checked={GROUP_BY_TO_ID[filterState.groupBy] === option.id}
                                onChange={() => {
                                    setFieldsChanged(true);
                                    setFilterState((prev) => ({
                                        ...prev,
                                        groupBy: ID_TO_GROUP_BY[option.id],
                                    }));
                                }}
                                sx={{ marginRight: 0 }}
                            />
                        ))}
                    </Box>
                </FormControl>
                <SimpleSingleSelect
                    name="exam-type-select"
                    label="Type"
                    value={filterState.examTypeMasterId}
                    onChange={(e: any) => {
                        setFieldsChanged(true);
                        const newExamTypeMasterId = Number(e.target.value);
                        const newFilterState = {
                            ...filterState,
                            examTypeMasterId: newExamTypeMasterId,
                        };
                        setFilterState(newFilterState);
                        // local filter if cached
                        if (examDataAll.length) {
                            filterExamsForSelection(
                                examDataAll,
                                newFilterState.schoolId,
                                newFilterState.classId,
                                newExamTypeMasterId
                            );
                        }
                    }}
                    options={examTypeMasterOptions}
                    sx={{ minWidth: 140, maxWidth: 280, height: 40, alignSelf: 'center' }}
                />
                <SimpleSingleSelect
                    name="exam-select"
                    label="Assessment"
                    value={filterState.examId}
                    onChange={(e: any) => {
                        setFieldsChanged(true);
                        setFilterState((prev) => ({ ...prev, examId: Number(e.target.value) }));
                    }}
                    options={examOptions}
                    sx={{ minWidth: 140, maxWidth: 280 }}
                />
                <SimpleButton
                    onClick={() => {
                        const missing = getMissingFields(filterState);
                        if (missing.length) {
                            setErrorMsg(`Please select: ${missing.join(', ')} before filtering.`);
                            return;
                        }
                        setErrorMsg(null);
                        setFieldsChanged(false);
                        refreshTableData(filterState);
                    }}
                    variant={fieldsChanged ? 'outlined' : 'contained'}
                    color="primary"
                    sx={{ minHeight: 40, height: 40, alignSelf: 'center', padding: '8px 25px' }}
                >
                    <FilterIcon
                        color={fieldsChanged ? ICON_COLORS.primary : ICON_COLORS.secondary}
                        style={{ width: '16px', height: '16px', marginRight: '8px' }}
                    />{' '}
                    Filter
                </SimpleButton>
            </FilterBarContainer>
            <ErrorMessage error={errorMsg} />
        </Box>
    );
};

export default StatusReportFilters;
